import { Button, DataButton, H6 } from "@sellar-io/components";
import { ReactNode, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";

import { Icon } from "../../../components";
import { Popover } from "../../../components/popover/Popover";
import { PopoverContent } from "../../../components/popover/PopoverContent";
import { PopoverTrigger } from "../../../components/popover/PopoverTrigger";
import { Tag } from "../../../components/tag/Tag";
import { SlideTransition } from "../../../components/transition/SlideTransition";
import { useSpotlightFilterStore } from "../SpotlightFilterStore";
import { useOnClickOutside } from "../../../hooks/useOnClickOutside";
import { zLayer, FloatingLayerProvider } from "@sellar-io/components";

const awardCategories = {
  Cask: [
    "Overall Champion Cask",
    "Session Dark Beer",
    "British Dark Beer",
    "British Bitter",
    "British Best Bitter",
    "Session Pale Ale",
    "Pale Ale",
    "IPA",
    "Speciality Light Beer",
    "Speciality Amber to Dark Beer",
    "Imperial & Strong Beer",
  ],
  Keg: [
    "Overall Champion Keg",
    "Non-Alcoholic & Low ABV",
    "Session Lager",
    "Premium Lager",
    "British Ales",
    "Session Pale Ale",
    "Pale Ale",
    "IPA",
    "Double & Triple IPA",
    "Speciality IPA",
    "Amber, Brown & Red Ale",
    "Stout & Porter",
    "Imperial & Strong Ale",
    "Sour & Wild Ale",
    "Continental Style Beer",
    "Speciality & Flavoured Beer",
  ],
  "Bottle & Can": [
    "Overall Champion Small Pack",
    "Non-Alcoholic & Low ABV",
    "Session Lager",
    "Premium Lager",
    "British Ales",
    "Session Pale Ale",
    "Pale Ale",
    "IPA",
    "Double & Triple IPA",
    "Speciality IPA",
    "Amber, Brown & Red Ale",
    "Stout & Porter",
    "Imperial & Strong Ale",
    "Sour & Wild Ale",
    "Continental Style Beer",
    "Speciality & Flavoured Beer",
  ],
} as const;

type AwardGroup = keyof typeof awardCategories;

const awardGroups: AwardGroup[] = ["Keg", "Cask", "Bottle & Can"];

interface AwardFilterContainerCardProps {
  onClick?: () => void;
  label?: string;
  children?: ReactNode;
  sticky?: boolean;
  mobileBorder?: boolean;
  desktopClassName?: string;
  className?: string;
}

const AwardCategoryList = ({
  group,
  selectedCategory,
  selectedGroup,
  onSelect,
}: {
  group: AwardGroup;
  selectedCategory: string | null;
  selectedGroup: AwardGroup | null;
  onSelect: (value: string | null, group: AwardGroup) => void;
}) => {
  const categories = awardCategories[group];
  const isSelected = selectedCategory !== null && selectedGroup === group;

  return (
    <div className="flex w-full flex-col gap-1 p-2 text-sm">
      {categories.map((category, i) => (
        <button
          key={i}
          type="button"
          onClick={() =>
            onSelect(
              isSelected && selectedCategory === category ? null : category,
              group,
            )
          }
          className="flex w-full cursor-pointer items-center gap-1 rounded-md px-2 py-1.5 text-left hover:bg-zinc-100"
        >
          {isSelected && selectedCategory === category ? (
            <Icon icon="CHECK" size={0.7} />
          ) : (
            <span className="w-[16.8px]" />
          )}
          {category}
        </button>
      ))}
    </div>
  );
};

export const AwardSpotlightFilterContainerCard = ({
  onClick,
  label,
  children,
  sticky = true,
  mobileBorder = false,
  desktopClassName,
  className,
}: AwardFilterContainerCardProps) => {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [selectedGroup, setSelectedGroup] = useState<AwardGroup | null>(null);
  const [mobileFilterActive, setMobileFilterActive] = useState(false);
  const [activePopovers, setActivePopovers] = useState<
    Record<AwardGroup, boolean>
  >({
    Cask: false,
    Keg: false,
    "Bottle & Can": false,
  });

  const popoverContainerRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(popoverContainerRef, () =>
    setActivePopovers({ Cask: false, Keg: false, "Bottle & Can": false }),
  );

  const search = useSpotlightFilterStore((state) => state.search);
  const setSearch = useSpotlightFilterStore((state) => state.setSearch);
  const setContainerTypes = useSpotlightFilterStore(
    (state) => state.setContainerTypes,
  );

  const getContainerTypesForGroup = (group: AwardGroup) => {
    switch (group) {
      case "Keg":
        return ["keg"] as const;
      case "Cask":
        return ["cask"] as const;
      case "Bottle & Can":
        return ["bottle", "can"] as const;
    }
  };

  const handleSelect = (value: string | null, group: AwardGroup) => {
    setSearch(value);
    setSelectedGroup(value ? group : null);
    setContainerTypes(value ? [...getContainerTypesForGroup(group)] : []);
    if (onClick) onClick();
    setActivePopovers({ Cask: false, Keg: false, "Bottle & Can": false });
  };

  return (
    <FloatingLayerProvider host={sticky ? "pinned" : "content"}>
      <div
        className={twMerge(
          "flex flex-col",
          sticky && `sticky top-[var(--nav-height)] ${zLayer.pinned}`,
          isDesktop && "bg-zinc-100",
          !isDesktop && "overflow-hidden",
          isDesktop && desktopClassName,
        )}
      >
        <div
          className={twMerge(
            "@container flex flex-col gap-2 rounded-lg border-b border-zinc-300 bg-zinc-50 p-4",
            (isDesktop || mobileBorder) && "border border-zinc-300",
            className,
          )}
        >
          <div className="flex flex-col items-start justify-between gap-2 @2xl:flex-row">
            <div className="flex flex-wrap items-center gap-2">
              {label && <H6>{label}</H6>}
              {!isDesktop ? (
                <Button
                  onClick={() => setMobileFilterActive(!mobileFilterActive)}
                  color="slim"
                  size="small"
                  className={twMerge(
                    "shadow-xs",
                    mobileFilterActive && "bg-zinc-200",
                  )}
                >
                  Award categories
                  <Icon icon="CHEVRON_DOWN" size={0.8} />
                </Button>
              ) : (
                <div
                  ref={popoverContainerRef}
                  className="flex flex-wrap items-center gap-2"
                >
                  {awardGroups.map((group) => {
                    const groupHasSelection = selectedGroup === group;

                    return (
                      <Popover
                        key={group}
                        trigger="click"
                        open={activePopovers[group]}
                        onOpenChange={(value) =>
                          setActivePopovers((prev) =>
                            value
                              ? {
                                  Cask: false,
                                  Keg: false,
                                  "Bottle & Can": false,
                                  [group]: true,
                                }
                              : { ...prev, [group]: false },
                          )
                        }
                        placement="bottom-start"
                        disableFlip
                        dismissOnOutsidePress={false}
                      >
                        <PopoverTrigger>
                          <DataButton
                            onClick={() =>
                              setActivePopovers((prev) => ({
                                Cask: false,
                                Keg: false,
                                "Bottle & Can": false,
                                [group]: !prev[group],
                              }))
                            }
                            border={true}
                            active={activePopovers[group]}
                            className={twMerge(
                              "min-h-[38.78px] rounded-md shadow-xs",
                              groupHasSelection && "bg-zinc-200",
                            )}
                          >
                            <Icon icon="CHEVRON_DOWN" />
                            {group}
                          </DataButton>
                        </PopoverTrigger>
                        <PopoverContent
                          portal={false}
                          className="mt-2 max-h-[250px] min-w-[380px] overflow-y-auto rounded-lg shadow-md"
                        >
                          <AwardCategoryList
                            group={group}
                            selectedCategory={search}
                            selectedGroup={selectedGroup}
                            onSelect={handleSelect}
                          />
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                </div>
              )}
            </div>
            {children}
          </div>
          {search && (
            <div className="flex w-full flex-row items-center gap-1 overflow-x-auto">
              <Tag
                className="mr-2"
                onClick={() => {
                  setSearch(null);
                  setSelectedGroup(null);
                  setContainerTypes([]);
                }}
              >
                {selectedGroup ? `${selectedGroup}: ${search}` : search}
              </Tag>
            </div>
          )}
        </div>
        <SlideTransition
          show={mobileFilterActive}
          className="w-full"
          parentClass="w-full bg-white"
        >
          <div className="flex max-h-[calc(100vh-var(--nav-height)-120px)] flex-col overflow-y-auto">
            {awardGroups.map((group) => (
              <div key={group} className="flex flex-col gap-1 p-4">
                <span className="font-semibold">{group}</span>
                <AwardCategoryList
                  group={group}
                  selectedCategory={search}
                  selectedGroup={selectedGroup}
                  onSelect={(value, g) => {
                    handleSelect(value, g);
                    setMobileFilterActive(false);
                  }}
                />
              </div>
            ))}
          </div>
        </SlideTransition>
      </div>
    </FloatingLayerProvider>
  );
};
